// components/GuidedCameraOverlay.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FiArrowLeft, FiPlus, FiX, FiCamera, FiUpload, FiVideo
} from 'react-icons/fi';
import { IoArrowUp, IoWarningOutline } from 'react-icons/io5';
import { RiGuideLine } from 'react-icons/ri';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';

const GuidedCameraOverlay = ({ 
  authUser, 
  onClose,
  onComplete,
  onSwitchToMyRenders,
  initialData = null,
  defaultImage = null,
}) => {
  // ========== ALL STATE DECLARATIONS FIRST ==========
  const [guidedImageUploaded, setGuidedImageUploaded] = useState(null);
  const [guidedImagePreview, setGuidedImagePreview] = useState(null);
  const [guidedMotionVideoBlob, setGuidedMotionVideoBlob] = useState(null);
  const [guidedMotionPreview, setGuidedMotionPreview] = useState(null);
  const [guidedMotionStoragePath, setGuidedMotionStoragePath] = useState(null);
  const [guidedMotionUrl, setGuidedMotionUrl] = useState(null);
  const [guidedMotionDuration, setGuidedMotionDuration] = useState(0);
  const [isUploadingMotion, setIsUploadingMotion] = useState(false);
  const [isMotionExpanded, setIsMotionExpanded] = useState(false);
  const [showGuidedMotionRecorder, setShowGuidedMotionRecorder] = useState(false);
  const [guidedRecordingDuration, setGuidedRecordingDuration] = useState(0);
  const [guidedMediaRecorder, setGuidedMediaRecorder] = useState(null);
  const [guidedIsRecording, setGuidedIsRecording] = useState(false);
  const [guidedMotionPreviewBlobUrl, setGuidedMotionPreviewBlobUrl] = useState(null);
  const [showGuidedMotionPreview, setShowGuidedMotionPreview] = useState(false);
  const [guidedCameraZoomLevel, setGuidedCameraZoomLevel] = useState(0.5);
  const [guidedIsCameraReady, setGuidedIsCameraReady] = useState(false);
  const [motionThumbnailUrl, setMotionThumbnailUrl] = useState(null);
  const [guidedCameraPermissionDenied, setGuidedCameraPermissionDenied] = useState(false);
  
  // Camera stream refs
  const guidedVideoRef = useRef(null);
  const guidedMotionVideoRef = useRef(null);
  const guidedMotionFileInputRef = useRef(null);
  const guidedRecorderStreamRef = useRef(null);
  const guidedFileInputRef = useRef(null);
  const guidedCameraStreamRef = useRef(null);

  // ========== THEN USEEFFECTS ==========
  // Load initial data when component mounts
  useEffect(() => {
    if (initialData) {
      if (initialData.imagePreview) {
        setGuidedImagePreview(initialData.imagePreview);
        setGuidedImageUploaded(initialData.imageFile);
      }
      if (initialData.motionUrl) {
        setGuidedMotionUrl(initialData.motionUrl);
        setGuidedMotionStoragePath(initialData.motionStoragePath);
        setGuidedMotionPreview(initialData.motionPreview);
        setGuidedMotionVideoBlob(initialData.motionBlob);
        setGuidedMotionDuration(initialData.motionDuration);
        if (initialData.motionThumbnail) {
          setMotionThumbnailUrl(initialData.motionThumbnail);
        }
      }
    }
  }, [initialData]);

  // Use defaultImage prop if initialData doesn't provide an image
  useEffect(() => {
    if (defaultImage && !guidedImagePreview && !initialData?.imagePreview) {
      setGuidedImagePreview(defaultImage);
      setGuidedImageUploaded(null);
    }
  }, [defaultImage, guidedImagePreview, initialData]);

  // Also update if initialData changes after mount
  useEffect(() => {
    if (initialData && initialData.imagePreview && !guidedImagePreview) {
      setGuidedImagePreview(initialData.imagePreview);
      setGuidedImageUploaded(initialData.imageFile);
    }
    if (initialData && initialData.motionUrl && !guidedMotionUrl) {
      setGuidedMotionUrl(initialData.motionUrl);
      setGuidedMotionStoragePath(initialData.motionStoragePath);
      setGuidedMotionPreview(initialData.motionPreview);
      setGuidedMotionVideoBlob(initialData.motionBlob);
      setGuidedMotionDuration(initialData.motionDuration);
      if (initialData.motionThumbnail) {
        setMotionThumbnailUrl(initialData.motionThumbnail);
      }
    }
  }, [initialData, guidedImagePreview, guidedMotionUrl]);
  
  // ============================================
  // GUIDED CAMERA INITIALIZATION
  // ============================================
  const initGuidedCamera = useCallback(async () => {
    // Stop any existing stream first with proper cleanup
    if (guidedCameraStreamRef.current) {
      const tracks = guidedCameraStreamRef.current.getTracks();
      tracks.forEach(track => {
        track.stop();
        track.enabled = false;
      });
      guidedCameraStreamRef.current = null;
    }
    
    // Clear video element srcObject
    if (guidedVideoRef.current) {
      guidedVideoRef.current.srcObject = null;
      guidedVideoRef.current.load();
    }
    
    // Small delay to ensure cleanup completes
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      // Apply default 0.5x zoom for background camera in guided mode
      try {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities?.() || {};
        if (capabilities.zoom) {
          const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        }
      } catch (zoomErr) {
        console.warn('Could not apply default zoom:', zoomErr);
      }
      
      guidedCameraStreamRef.current = stream;
      
      if (guidedVideoRef.current) {
        guidedVideoRef.current.srcObject = stream;
        guidedVideoRef.current.setAttribute('playsinline', 'true');
        guidedVideoRef.current.muted = true;
        
        guidedVideoRef.current.onloadedmetadata = async () => {
          try {
            if (guidedVideoRef.current && guidedVideoRef.current.readyState >= 2) {
              await guidedVideoRef.current.play();
            }
          } catch (playError) {
            console.warn('Autoplay prevented:', playError);
          }
          setGuidedIsCameraReady(true);
        };
      }
    } catch (error) {
      console.error('Guided camera access error:', error);
      setGuidedCameraPermissionDenied(true);
    }
  }, []);
  
  // Auto-initialize camera when component mounts
  useEffect(() => {
    initGuidedCamera();
    
    // NUCLEAR CLEANUP on unmount - stops ALL camera activity
            return () => {
            const forceStopStream = (streamRef) => {
                if (streamRef.current) {
                const stream = streamRef.current;
                stream.getTracks().forEach(track => {
                    track.stop();
                    track.enabled = false;
                });
                // Also try getTracks again after a microtask to catch any late tracks
                setTimeout(() => {
                    stream.getTracks().forEach(track => {
                    track.stop();
                    track.enabled = false;
                    });
                }, 50);
                streamRef.current = null;
                }
            };
            
            forceStopStream(guidedCameraStreamRef);
            forceStopStream(guidedRecorderStreamRef);
            
            // Clear video element srcObjects and force release
            if (guidedVideoRef.current) {
                guidedVideoRef.current.srcObject = null;
                guidedVideoRef.current.removeAttribute('src');
                guidedVideoRef.current.load();
            }
            if (guidedMotionVideoRef.current) {
                guidedMotionVideoRef.current.srcObject = null;
                guidedMotionVideoRef.current.removeAttribute('src');
                guidedMotionVideoRef.current.load();
            }
            
            // Stop any active recording
            if (guidedMediaRecorder && guidedMediaRecorder.state === 'recording') {
                guidedMediaRecorder.stop();
            }
            
            // Revoke blob URLs
            if (guidedMotionPreviewBlobUrl) {
                URL.revokeObjectURL(guidedMotionPreviewBlobUrl);
            }
            };
  }, [initGuidedCamera]);

  // ============================================
  // THUMBNAIL EXTRACTION HELPER
  // ============================================
  const extractThumbnailFromBlob = (blob) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      const url = URL.createObjectURL(blob);
      video.src = url;
      
      let resolved = false;
      
      const captureFrame = () => {
        if (resolved) return;
        resolved = true;
        try {
          const canvas = document.createElement('canvas');
          const size = Math.min(video.videoWidth || 720, video.videoHeight || 720);
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          const sx = ((video.videoWidth || 720) - size) / 2;
          const sy = ((video.videoHeight || 720) - size) / 2;
          ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          URL.revokeObjectURL(url);
          resolve(dataUrl);
        } catch (e) {
          URL.revokeObjectURL(url);
          reject(e);
        }
      };
      
      video.onloadeddata = () => {
        if (video.duration) {
          video.currentTime = Math.min(0.5, video.duration / 2);
        } else {
          captureFrame();
        }
      };
      
      video.onseeked = captureFrame;
      
      setTimeout(() => {
        if (!resolved) {
          captureFrame();
        }
      }, 1000);
      
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load video for thumbnail'));
      };
    });
  };
  
  // ============================================
  // GUIDED IMAGE UPLOAD HANDLER
  // ============================================
  const handleGuidedImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setGuidedImagePreview(reader.result);
      setGuidedImageUploaded(file);
    };
    reader.readAsDataURL(file);
  };
  
  const handleGuidedImageClear = () => {
    setGuidedImagePreview(null);
    setGuidedImageUploaded(null);
  };
  
  // ============================================
  // GUIDED MOTION REFERENCE RECORDING
  // ============================================
  const startGuidedMotionRecording = () => {
    if (guidedIsRecording) return;
    
    if (guidedMotionPreviewBlobUrl) {
      URL.revokeObjectURL(guidedMotionPreviewBlobUrl);
      setGuidedMotionPreviewBlobUrl(null);
    }
    
    setGuidedMotionVideoBlob(null);
    setGuidedMotionPreview(null);
    setGuidedMotionUrl(null);
    setGuidedMotionStoragePath(null);
    setGuidedMotionDuration(0);
    setShowGuidedMotionPreview(false);
    setGuidedRecordingDuration(0);
    setGuidedIsRecording(true);
    
    const chunks = [];
    const startTime = Date.now();
    let isRecording = true;
    
    const timerInterval = setInterval(() => {
      if (!isRecording) {
        clearInterval(timerInterval);
        return;
      }
      const elapsed = (Date.now() - startTime) / 1000;
      setGuidedRecordingDuration(parseFloat(elapsed.toFixed(1)));
      if (elapsed >= 15) {
        clearInterval(timerInterval);
        isRecording = false;
        if (guidedMediaRecorder && guidedMediaRecorder.state === 'recording') {
          guidedMediaRecorder.stop();
        }
      }
    }, 100);
    
    const recorderStream = guidedRecorderStreamRef.current;
    if (!recorderStream) {
      console.error('No recorder stream available');
      setGuidedIsRecording(false);
      alert('Camera stream is not available. Please close and reopen the camera.');
      return;
    }
    
    const videoTracks = recorderStream.getVideoTracks();
    if (videoTracks.length === 0 || videoTracks[0].readyState !== 'live') {
      console.error('No live video track for guided recording');
      setGuidedIsRecording(false);
      alert('Camera not ready. Please try again.');
      return;
    }
    
    let mimeType = '';
    const candidates = [
      'video/webm;codecs=vp8',
      'video/webm;codecs=vp9',
      'video/webm',
      'video/mp4'
    ];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        break;
      }
    }
    
    const recorderOptions = mimeType ? { mimeType, videoBitsPerSecond: 2500000 } : { videoBitsPerSecond: 2500000 };
    const recorder = new MediaRecorder(recorderStream, recorderOptions);
    
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    
    recorder.onerror = (e) => {
      console.error('Guided MediaRecorder error:', e);
      isRecording = false;
      clearInterval(timerInterval);
      setGuidedIsRecording(false);
      alert('Recording failed. Please try again.');
    };
    
    recorder.onstop = () => {
      isRecording = false;
      clearInterval(timerInterval);
      setGuidedIsRecording(false);
      
      if (chunks.length === 0) {
        console.warn('No data recorded');
        alert('No video data was recorded. Please try again.');
        return;
      }
      
      const blobType = recorder.mimeType || 'video/webm';
      const blob = new Blob(chunks, { type: blobType });
      setGuidedMotionVideoBlob(blob);
      
      const previewUrl = URL.createObjectURL(blob);
      setGuidedMotionPreviewBlobUrl(previewUrl);
      setGuidedMotionPreview(previewUrl);
      
      const recordedDuration = parseFloat(((Date.now() - startTime) / 1000).toFixed(1));
      setGuidedMotionDuration(recordedDuration);
      
      const durationCheckUrl = URL.createObjectURL(blob);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.playsInline = true;
      video.muted = true;
      video.onloadedmetadata = () => {
        if (video.duration && video.duration > 0 && video.duration !== Infinity) {
          setGuidedMotionDuration(parseFloat(video.duration.toFixed(1)));
        }
        URL.revokeObjectURL(durationCheckUrl);
      };
      video.onerror = () => {
        console.warn('Could not load video metadata, using timer duration');
        URL.revokeObjectURL(durationCheckUrl);
      };
      video.src = durationCheckUrl;
      
      setShowGuidedMotionPreview(true);
    };
    
    setGuidedMediaRecorder(recorder);
    
    try {
      recorder.start(1000);
      console.log('Guided MediaRecorder started successfully');
    } catch (error) {
      console.error('Failed to start guided MediaRecorder:', error);
      isRecording = false;
      clearInterval(timerInterval);
      setGuidedIsRecording(false);
      alert('Failed to start recording. Please try uploading a video instead.');
    }
  };
  
  const stopGuidedMotionRecording = () => {
    if (guidedMediaRecorder && guidedMediaRecorder.state === 'recording') {
      try {
        guidedMediaRecorder.requestData();
      } catch (e) {
        console.warn('requestData failed:', e);
      }
      
      setTimeout(() => {
        if (guidedMediaRecorder && guidedMediaRecorder.state === 'recording') {
          try {
            guidedMediaRecorder.stop();
            console.log('Guided MediaRecorder stopped successfully');
          } catch (e) {
            console.error('Guided MediaRecorder stop failed:', e);
            setGuidedIsRecording(false);
          }
        }
      }, 300);
    } else {
      setGuidedIsRecording(false);
    }
  };
  
  const uploadGuidedMotionVideoToStorage = async (blob) => {
    if (!authUser) return null;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Video = reader.result;
          const storage = getStorage();
          const path = `users/${authUser.uid}/motion-references/${Date.now()}.mp4`;
          const storageRef = ref(storage, path);
          
          await uploadString(storageRef, base64Video, 'data_url');
          const permanentUrl = await getDownloadURL(storageRef);
          
          setGuidedMotionStoragePath(path);
          setGuidedMotionUrl(permanentUrl);
          resolve(permanentUrl);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  const handleConfirmGuidedMotion = async () => {
  if (!guidedMotionVideoBlob) return;
  
  setIsUploadingMotion(true); // Start loading
  
  try {
    const permanentUrl = await uploadGuidedMotionVideoToStorage(guidedMotionVideoBlob);
    if (permanentUrl) {
      if (guidedMotionPreviewBlobUrl) {
        URL.revokeObjectURL(guidedMotionPreviewBlobUrl);
        setGuidedMotionPreviewBlobUrl(null);
      }
      if (guidedMotionPreview && guidedMotionPreview !== guidedMotionPreviewBlobUrl) {
        URL.revokeObjectURL(guidedMotionPreview);
      }
      setShowGuidedMotionPreview(false);
      setShowGuidedMotionRecorder(false);
      
      if (guidedMotionVideoBlob) {
        extractThumbnailFromBlob(guidedMotionVideoBlob)
          .then(thumb => setMotionThumbnailUrl(thumb))
          .catch(err => console.warn('Thumbnail extraction failed:', err));
      }
      
      // Stop only the recorder stream
      if (guidedRecorderStreamRef.current) {
        guidedRecorderStreamRef.current.getTracks().forEach(track => track.stop());
        guidedRecorderStreamRef.current = null;
      }
    }
  } catch (error) {
    console.error('Failed to upload guided motion video:', error);
    alert('Failed to save motion reference. Please try again.');
  } finally {
    setIsUploadingMotion(false); // Stop loading
    // Reset zoom back to default 0.5x
    setGuidedCameraZoomLevel(0.5);
  }
};
  
  const handleReRecordGuidedMotion = async () => {
    // Reset main camera zoom to 0.5x before starting fresh
    if (guidedCameraStreamRef.current) {
      const videoTrack = guidedCameraStreamRef.current.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities?.() || {};
      if (capabilities.zoom) {
        const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
        await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] }).catch(err => console.warn('Failed to reset main camera zoom:', err));
      }
    }
    
    if (guidedMotionPreviewBlobUrl) {
      URL.revokeObjectURL(guidedMotionPreviewBlobUrl);
      setGuidedMotionPreviewBlobUrl(null);
    }
    setGuidedMotionVideoBlob(null);
    setGuidedMotionPreview(null);
    setGuidedMotionDuration(0);
    setShowGuidedMotionPreview(false);
    setGuidedRecordingDuration(0);
    setGuidedIsRecording(false);
    // Reset zoom back to 0.5x
    setGuidedCameraZoomLevel(0.5);
    
    // Stop the old recorder stream and get a fresh one
    if (guidedRecorderStreamRef.current) {
      guidedRecorderStreamRef.current.getTracks().forEach(track => track.stop());
      guidedRecorderStreamRef.current = null;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      // Apply default 0.5x zoom for background camera in guided mode
      try {
        const videoTrack = stream.getVideoTracks()[0]; 
        const capabilities = videoTrack.getCapabilities?.() || {};
        if (capabilities.zoom) {
          const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        }
      } catch (zoomErr) {
        console.warn('Could not apply default zoom:', zoomErr);
      }
      
      guidedRecorderStreamRef.current = stream;
      
      // Apply 0.5x zoom to the fresh stream
      try {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities?.() || {};
        if (capabilities.zoom) {
          const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        }
      } catch (zoomErr) {
        console.warn('Could not apply zoom on re-record:', zoomErr);
      }
      
      if (guidedMotionVideoRef.current) {
        guidedMotionVideoRef.current.srcObject = stream;
        guidedMotionVideoRef.current.play().catch(e => console.warn('play error on re-record:', e));
      }
      
      setGuidedIsCameraReady(true);
      setShowGuidedMotionRecorder(true);
    } catch (error) {
      console.error('Failed to get new stream for re-record:', error);
      setGuidedCameraPermissionDenied(true);
    }
  };
  
  const handleCancelGuidedMotion = () => {
    if (guidedMediaRecorder && guidedMediaRecorder.state === 'recording') {
      guidedMediaRecorder.stop();
    }
    if (guidedMotionPreviewBlobUrl) {
      URL.revokeObjectURL(guidedMotionPreviewBlobUrl);
      setGuidedMotionPreviewBlobUrl(null);
    }
    if (guidedRecorderStreamRef.current) {
      guidedRecorderStreamRef.current.getTracks().forEach(track => track.stop());
      guidedRecorderStreamRef.current = null;
    }
    setGuidedIsRecording(false);
    setGuidedRecordingDuration(0);
    setShowGuidedMotionRecorder(false);
    setShowGuidedMotionPreview(false);
    setGuidedMotionVideoBlob(null);
    setGuidedMotionPreview(null);
    // Reset zoom back to default 0.5x AND apply it to the main camera stream
    setGuidedCameraZoomLevel(0.5);
    
    // Also update the main camera stream's zoom to 0.5x
    if (guidedCameraStreamRef.current) {
      const videoTrack = guidedCameraStreamRef.current.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities?.() || {};
      if (capabilities.zoom) {
        const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
        videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] })
          .catch((err) => console.warn('Failed to reset main camera zoom:', err));
      }
    }
  };
  
  const handleOpenGuidedMotionRecorder = async () => {
    setShowGuidedMotionRecorder(true);
    setGuidedRecordingDuration(0);
    setGuidedIsRecording(false);
    setGuidedMotionVideoBlob(null);
    setGuidedMotionPreview(null);
    setGuidedMotionPreviewBlobUrl(null);
    setGuidedMotionDuration(0);
    setGuidedIsCameraReady(false);
    // Always start at 0.5x zoom
    setGuidedCameraZoomLevel(0.5);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      // Apply default 0.5x zoom for background camera in guided mode
      try {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities?.() || {};
        if (capabilities.zoom) {
          const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        }
      } catch (zoomErr) {
        console.warn('Could not apply default zoom:', zoomErr);
      }
      
      guidedRecorderStreamRef.current = stream;
      
      // Apply 0.5x zoom to the fresh stream
      try {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities?.() || {};
        if (capabilities.zoom) {
          const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        }
      } catch (zoomErr) {
        console.warn('Could not apply zoom on re-record:', zoomErr);
      }
      
      if (guidedMotionVideoRef.current) {
        guidedMotionVideoRef.current.srcObject = stream;
        guidedMotionVideoRef.current.muted = true;
        guidedMotionVideoRef.current.playsInline = true;
        await guidedMotionVideoRef.current.play();
      }
      
      // Apply default 0.5x zoom to the camera
      try {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities?.() || {};
        if (capabilities.zoom) {
          const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        }
      } catch (zoomErr) {
        console.warn('Could not apply default zoom:', zoomErr);
      }
      
      setGuidedIsCameraReady(true);
    } catch (error) {
      console.error('Guided motion camera access error:', error);
      setGuidedCameraPermissionDenied(true);
    }
  };
  
  const handleGuidedMotionFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file (MP4, MOV, etc.)');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) {
      alert('Video must be less than 50MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Video = reader.result;
      setGuidedMotionVideoBlob(file);
      const previewUrl = URL.createObjectURL(file);
      setGuidedMotionPreview(previewUrl);
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        setGuidedMotionDuration(video.duration);
        URL.revokeObjectURL(video.src);
      };
      video.src = URL.createObjectURL(file);
      
      try {
        const storage = getStorage();
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop() || 'mp4';
        const path = `users/${authUser.uid}/motion-references/${timestamp}.${fileExtension}`;
        const storageRef = ref(storage, path);
        await uploadString(storageRef, base64Video, 'data_url');
        const permanentUrl = await getDownloadURL(storageRef);
        setGuidedMotionStoragePath(path);
        setGuidedMotionUrl(permanentUrl);
        extractThumbnailFromBlob(file)
          .then(thumb => setMotionThumbnailUrl(thumb))
          .catch(err => console.warn('Thumbnail extraction failed:', err));
        setShowGuidedMotionRecorder(false);
        // Reset zoom back to 0.5x
        setGuidedCameraZoomLevel(0.5);
        if (guidedRecorderStreamRef.current) {
          guidedRecorderStreamRef.current.getTracks().forEach(track => track.stop());
          guidedRecorderStreamRef.current = null;
        }
      } catch (error) {
        console.error('Failed to upload guided motion video:', error);
        alert('Failed to save motion reference. Please try again.');
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  
  // Check if input is ready
  const isGuidedInputComplete = guidedImagePreview;
  
const handleComplete = () => {
  console.log('GuidedCameraOverlay: handleComplete', {
    hasImagePreview: !!guidedImagePreview,
    hasMotionUrl: !!guidedMotionUrl
  });
  
  // Stop all camera streams
  if (guidedCameraStreamRef.current) {
    guidedCameraStreamRef.current.getTracks().forEach(track => {
      track.stop();
      track.enabled = false;
    });
    guidedCameraStreamRef.current = null;
  }
  if (guidedRecorderStreamRef.current) {
    guidedRecorderStreamRef.current.getTracks().forEach(track => {
      track.stop();
      track.enabled = false;
    });
    guidedRecorderStreamRef.current = null;
  }
  if (guidedVideoRef.current) {
    guidedVideoRef.current.srcObject = null;
    guidedVideoRef.current.pause();
    guidedVideoRef.current.load();
  }
  if (guidedMotionVideoRef.current) {
    guidedMotionVideoRef.current.srcObject = null;
    guidedMotionVideoRef.current.pause();
    guidedMotionVideoRef.current.load();
  }
  
  setTimeout(() => {
    onComplete({
      imagePreview: guidedImagePreview,
      imageFile: guidedImageUploaded,
      motionUrl: guidedMotionUrl,
      motionStoragePath: guidedMotionStoragePath,
      motionPreview: guidedMotionPreview,
      motionBlob: guidedMotionVideoBlob,
      motionDuration: guidedMotionDuration,
      motionThumbnail: motionThumbnailUrl,
    });
  }, 50);
};
  
  
  return (
    <>
      <div className="guided-camera-overlay">
        {/* Camera Preview */}
        <div className="guided-camera-preview">
          <video
            ref={guidedVideoRef}
            autoPlay
            playsInline
            muted
            className="guided-camera-video"
            style={{ opacity: guidedIsCameraReady ? 1 : 0 }}
          />
          {!guidedIsCameraReady && !guidedCameraPermissionDenied && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 5,
            }}>
              <div style={{ textAlign: 'center', color: 'white' }}>
                <div style={{ width: '40px', height: '40px', border: '3px solid white', borderTop: '3px solid #88b7ff', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
                <p style={{ fontFamily: 'Avenir, sans-serif' }}>Initializing camera...</p>
              </div>
            </div>
          )}
          {guidedCameraPermissionDenied && (
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', zIndex: 5, flexDirection: 'column', gap: '20px',
            }}>
                <p style={{ fontFamily: 'Avenir, sans-serif', color: 'white', textAlign: 'center', padding: '20px' }}>
                Camera access denied. Please enable camera permissions in your browser settings.
                </p>
                <button onClick={() => {
                // Stop any streams that may exist
                if (guidedCameraStreamRef.current) {
                    guidedCameraStreamRef.current.getTracks().forEach(track => { track.stop(); track.enabled = false; });
                    guidedCameraStreamRef.current = null;
                }
                if (guidedRecorderStreamRef.current) {
                    guidedRecorderStreamRef.current.getTracks().forEach(track => { track.stop(); track.enabled = false; });
                    guidedRecorderStreamRef.current = null;
                }
                onClose();
                }} style={{
                padding: '12px 24px', background: '#88b7ff', border: 'none',
                borderRadius: '30px', color: 'white', fontFamily: 'Avenir, sans-serif', cursor: 'pointer',
                }}>
                Back to Dashboard
                </button>
            </div>
            )}
        </div>
        
        {/* Back Button - Goes to Dashboard */}
                <button className="guided-back-btn" onClick={() => {
                // Stop all camera streams immediately and hide overlay
                if (guidedCameraStreamRef.current) {
                    guidedCameraStreamRef.current.getTracks().forEach(track => { track.stop(); track.enabled = false; });
                    guidedCameraStreamRef.current = null;
                }
                if (guidedRecorderStreamRef.current) {
                    guidedRecorderStreamRef.current.getTracks().forEach(track => { track.stop(); track.enabled = false; });
                    guidedRecorderStreamRef.current = null;
                }
                if (guidedVideoRef.current) {
                    guidedVideoRef.current.srcObject = null;
                    guidedVideoRef.current.pause();
                    guidedVideoRef.current.load();
                }
                if (guidedMotionVideoRef.current) {
                    guidedMotionVideoRef.current.srcObject = null;
                    guidedMotionVideoRef.current.pause();
                    guidedMotionVideoRef.current.load();
                }
                // Close guided overlay
                onClose();
                }}
                style={{ top: 'calc(20px + env(safe-area-inset-top, 0px))' }}>
                <FiArrowLeft size={16} />
                Back
                </button>
        
        {/* Title */}
        <div className="guided-title">Create New Render</div>
        
        {/* Inputs Overlay */}
        <div className="guided-inputs-overlay">
          <div className="guided-cards-container">
            <div className={`guided-input-card ${isMotionExpanded ? 'motion-expanded' : ''}`}>
              {/* Image Source Section */}
              <div className="guided-input-label">
                <FiCamera size={12} />
                Image Source
              </div>
              <div className="guided-image-upload-area">
                <div 
                className={`guided-image-box ${guidedImagePreview ? 'has-image' : 'marching-ants'}`}
                onClick={() => { 
                  if (!guidedImagePreview || guidedImagePreview !== defaultImage) {
                    guidedFileInputRef.current?.click(); 
                  }
                }}
                style={{ position: 'relative', cursor: guidedImagePreview === defaultImage ? 'default' : 'pointer' }}
              >
                {!guidedImagePreview && (
                  <div className="marching-ants-border">
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible', width: '100%', height: '100%' }}>
                      <rect x="2" y="2" width="calc(100% - 4px)" height="calc(100% - 4px)" rx="14" ry="14" />
                    </svg>
                  </div>
                )}
                {guidedImagePreview ? (
                  <>
                    <img src={guidedImagePreview} alt="Upload preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px' }} />
                    {/* Only show X button for non-canvas images */}
                    {guidedImagePreview !== defaultImage && (
                      <button onClick={(e) => { e.stopPropagation(); handleGuidedImageClear(); }} style={{
                        position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)',
                        border: 'none', borderRadius: '50%', width: '24px', height: '24px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: 'white', zIndex: 10,
                      }}>
                        <FiX size={12} />
                      </button>
                    )}
                  </>
                ) : (
                  <FiPlus size={32} color="#ffffffff" />
                )}
              </div>
              </div>
              <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', textAlign: 'center', marginBottom: '24px' }}>
                {!guidedImagePreview && 'Upload Image Source'}
              </div>
              
              {/* Divider */}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)', margin: '0 0 20px 0' }} />
              
              {/* Motion Paths Section */}
              <div className="guided-input-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <RiGuideLine size={12} />
                  <span style={{ fontSize: '0.7rem' }}>Motion Paths</span>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  <span style={{ 
                    fontSize: '0.5rem', 
                    color: guidedMotionUrl ? '#88b7ff' : 'rgba(255,255,255,0.6)', 
                    background: guidedMotionUrl ? 'rgba(136, 183, 255, 0.2)' : 'rgba(255,255,255,0.04)', 
                    padding: '2px 6px', borderRadius: '12px', whiteSpace: 'nowrap',
                    fontWeight: guidedMotionUrl ? 500 : 400,
                  }}>{guidedMotionUrl ? 'Active' : 'Optional'}</span>
                  <span style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)', background: 'rgba(255, 255, 255, 0.04)', padding: '2px 6px', borderRadius: '12px', whiteSpace: 'nowrap' }}>Camera Dynamics</span>
                </div>
              </div>
              
              {!guidedMotionUrl ? (
                <div className={`guided-motion-container ${isMotionExpanded ? 'expanded' : ''}`}>
                  <div 
                    className={`guided-motion-trigger-box 
                      ${guidedMotionUrl ? 'has-motion' : ''} 
                      ${guidedImagePreview && !guidedMotionUrl ? 'marching-ants' : ''} 
                      ${!guidedImagePreview ? 'disabled' : ''}`}
                    onClick={() => {
                      if (guidedImagePreview && !guidedMotionUrl) {
                        setIsMotionExpanded(!isMotionExpanded);
                      }
                    }}
                    style={{ position: 'relative' }}
                  >
                    {guidedImagePreview && !guidedMotionUrl && (
                      <div className="marching-ants-border">
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible', width: '100%', height: '100%' }}>
                          <rect x="2" y="2" width="calc(100% - 4px)" height="calc(100% - 4px)" rx="14" ry="14" />
                        </svg>
                      </div>
                    )}
                    {isMotionExpanded ? (
                      <>
                        <IoArrowUp size={32} color="#ffffffff" />
                        <span style={{ fontSize: '0.65rem', color: 'white', marginTop: '8px' }}>Back</span>
                      </>
                    ) : (
                      <FiVideo size={32} color={guidedImagePreview ? "#ffffffff" : "rgba(255,255,255,0.3)"} />
                    )}
                  </div>
                  
                  <div className={`guided-motion-buttons-wrapper ${isMotionExpanded ? 'expanded' : ''}`}>
                    <div className="guided-motion-area">
                      <button className="guided-motion-button" onClick={(e) => { e.stopPropagation(); handleOpenGuidedMotionRecorder(); setIsMotionExpanded(false); }}>
                        <FiCamera size={20} color="#ffffffff" />
                        <span>Record</span>
                      </button>
                      <button className="guided-motion-button" onClick={(e) => { e.stopPropagation(); guidedMotionFileInputRef.current?.click(); setIsMotionExpanded(false); }}>
                        <FiUpload size={20} color="#ffffffff" />
                        <span>Upload</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                  <div style={{ width: '120px', height: '150px', borderRadius: '16px', overflow: 'hidden', border: '2px solid #88b7ff', position: 'relative', background: '#1a1a1a' }}>
                    {motionThumbnailUrl ? (
                      <img src={motionThumbnailUrl} alt="Motion thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FiVideo size={32} color="#88b7ff" />
                      </div>
                    )}
                    <div style={{ position: 'absolute', bottom: '6px', left: '6px', background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '0.65rem', padding: '2px 6px', borderRadius: '8px', fontFamily: 'Avenir, sans-serif' }}>
                      {guidedMotionDuration ? `${guidedMotionDuration.toFixed(1)}s` : 'Ready'}
                    </div>
                    <button onClick={() => {
                      setGuidedMotionUrl(null);
                      setGuidedMotionPreview(null);
                      setGuidedMotionVideoBlob(null);
                      setMotionThumbnailUrl(null);
                      setIsMotionExpanded(false);
                    }} style={{
                      position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)',
                      border: 'none', borderRadius: '50%', width: '24px', height: '24px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: 'white', zIndex: 10,
                    }}>
                      <FiX size={12} />
                    </button>
                  </div>
                </div>
              )}
              
              <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginTop: '12px' }}>
                {!guidedImagePreview ? 'Add Image Source to Continue' : (!guidedMotionUrl && 'Input Camera Motion: Up to 15s')}
              </div>
            </div>
          </div>
          
          {/* Buttons Container */}
          <div className="guided-button-container" style={{ flexDirection: 'column', gap: '8px' }}>
            {guidedImagePreview && guidedMotionUrl && (
              <div style={{ textAlign: 'center', color: 'white', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', marginBottom: '4px', animation: 'blink 1.5s ease-in-out infinite' }}>
                Click continue to add Prompt
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <button 
                className={`guided-action-btn continue-btn ${!isGuidedInputComplete ? 'disabled' : ''}`}
                onClick={handleComplete}
                disabled={!isGuidedInputComplete}
                style={{
                  background: isGuidedInputComplete ? '#88b7ff' : 'rgba(255, 255, 255, 0.15)',
                  border: isGuidedInputComplete ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white', fontWeight: isGuidedInputComplete ? 600 : 400,
                  transition: 'background 0.8s ease, border 0.8s ease, font-weight 0.8s ease',
                }}
              >
                Continue
              </button>
              <button 
                className="guided-action-btn my-renders-btn"
                onClick={() => {
                    // Stop all guided camera streams before exiting
                    if (guidedCameraStreamRef.current) {
                    guidedCameraStreamRef.current.getTracks().forEach(track => { track.stop(); track.enabled = false; });
                    guidedCameraStreamRef.current = null;
                    }
                    if (guidedRecorderStreamRef.current) {
                    guidedRecorderStreamRef.current.getTracks().forEach(track => { track.stop(); track.enabled = false; });
                    guidedRecorderStreamRef.current = null;
                    }
                    if (guidedVideoRef.current) {
                    guidedVideoRef.current.srcObject = null;
                    guidedVideoRef.current.removeAttribute('src');
                    guidedVideoRef.current.load();
                    }
                    if (guidedMotionVideoRef.current) {
                    guidedMotionVideoRef.current.srcObject = null;
                    guidedMotionVideoRef.current.removeAttribute('src');
                    guidedMotionVideoRef.current.load();
                    }
                    if (guidedMediaRecorder && guidedMediaRecorder.state === 'recording') {
                    guidedMediaRecorder.stop();
                    }
                    if (guidedMotionPreviewBlobUrl) {
                    URL.revokeObjectURL(guidedMotionPreviewBlobUrl);
                    }
                    
                    // Pass data directly to onSwitchToMyRenders instead of onComplete
                    onSwitchToMyRenders({
                    imagePreview: guidedImagePreview,
                    imageFile: guidedImageUploaded,
                    motionUrl: guidedMotionUrl,
                    motionStoragePath: guidedMotionStoragePath,
                    motionPreview: guidedMotionPreview,
                    motionBlob: guidedMotionVideoBlob,
                    motionDuration: guidedMotionDuration,
                    motionThumbnail: motionThumbnailUrl,
                    });
                }}
                >
                My Renders
                </button>
            </div>
          </div>
        </div>
        
        {/* Hidden file inputs */}
        <input type="file" ref={guidedFileInputRef} accept="image/*" style={{ display: 'none' }} onChange={handleGuidedImageUpload} />
        <input type="file" ref={guidedMotionFileInputRef} accept="video/mp4,video/quicktime,video/webm" style={{ display: 'none' }} onChange={handleGuidedMotionFileUpload} />
      </div>
      
      {/* GUIDED MOTION RECORDER MODAL */}
      {showGuidedMotionRecorder && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'black', zIndex: 3000, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <video
              ref={guidedMotionVideoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          
          {/* Permission denied overlay */}
          {guidedCameraPermissionDenied && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 15, flexDirection: 'column', gap: '20px',
            }}>
              <p style={{ fontFamily: 'Avenir, sans-serif', color: 'white', textAlign: 'center', padding: '20px' }}>
                Camera access denied. Please enable camera permissions in your browser settings, or upload a video instead.
              </p>
              <button onClick={() => guidedMotionFileInputRef.current?.click()} style={{
                padding: '12px 24px', background: '#88b7ff', border: 'none',
                borderRadius: '30px', color: 'white', fontFamily: 'Avenir, sans-serif', cursor: 'pointer',
              }}>
                Upload Video Instead
              </button>
              <button onClick={() => {
                setGuidedCameraZoomLevel(0.5);
                handleCancelGuidedMotion();
              }} style={{
                padding: '12px 24px', background: 'transparent', border: '1px solid white',
                borderRadius: '30px', color: 'white', fontFamily: 'Avenir, sans-serif', cursor: 'pointer',
              }}>
                Cancel
              </button>
            </div>
          )}
          
             <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
              padding: '24px 20px calc(40px + env(safe-area-inset-bottom, 0px))', color: 'white', zIndex: 10,
            }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.8rem', fontFamily: 'Avenir, sans-serif' }}>
                <span>{guidedRecordingDuration.toFixed(1)}s</span>
                <span></span>
              </div>
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.3)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${(guidedRecordingDuration / 15) * 100}%`,
                  height: '100%',
                  background: guidedRecordingDuration >= 7 ? '#4CAF50' : '#88b7ff',
                  transition: 'width 0.05s linear',
                }} />
              </div>
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '28px', marginBottom: '8px' }}>
                <span style={{ animation: 'slideLeft 1s ease infinite' }}>←</span>
                <span style={{ animation: 'slideRight 1s ease infinite 0.2s' }}>→</span>
                <span style={{ animation: 'slideUp 1s ease infinite 0.4s' }}>↑</span>
                <span style={{ animation: 'slideDown 1s ease infinite 0.6s' }}>↓</span>
              </div>
              <p style={{ fontSize: '0.85rem', fontFamily: 'Avenir, sans-serif', marginBottom: '6px' }}>
                Move your device through space to control motion
              </p>
              {guidedRecordingDuration > 0 && guidedRecordingDuration < 7 && (
                <p style={{ fontSize: '0.65rem', color: '#ffaa00', marginTop: '6px' }}>
                  Need {(7 - guidedRecordingDuration).toFixed(1)} more seconds: Minimum 7s required
                </p>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', alignItems: 'center' }}>
              <button onClick={() => {
                setGuidedCameraZoomLevel(0.5);
                handleCancelGuidedMotion();
              }} style={{
                padding: '10px 20px', background: 'rgba(255,255,255,0.2)', border: 'none',
                borderRadius: '30px', color: 'white', fontSize: '0.85rem',
                fontFamily: 'Avenir, sans-serif', cursor: 'pointer',
              }}>
                Cancel
              </button>
              
              <button onClick={guidedIsRecording ? stopGuidedMotionRecording : startGuidedMotionRecording} style={{
                width: '70px', height: '70px', borderRadius: '50%',
                background: guidedIsRecording ? '#ff3b30' : '#818181ff',
                border: 'none', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.1s ease', flexShrink: 0,
              }}>
                {guidedIsRecording ? (
                  <div style={{ width: '28px', height: '28px', background: 'white', borderRadius: '4px' }} />
                ) : (
                  <div style={{ width: '58px', height: '58px', background: 'white', borderRadius: '50%' }} />
                )}
              </button>
              
                              <button
                onClick={() => {
                  const newZoom = guidedCameraZoomLevel === 0.5 ? 1 : 0.5;
                  setGuidedCameraZoomLevel(newZoom);
                  if (guidedRecorderStreamRef.current) {
                    const videoTrack = guidedRecorderStreamRef.current.getVideoTracks()[0];
                    const capabilities = videoTrack.getCapabilities?.() || {};
                    if (capabilities.zoom) {
                      const clampedZoom = Math.min(Math.max(newZoom, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
                      videoTrack.applyConstraints({ advanced: [{ zoom: clampedZoom }] })
                        .catch((err) => console.warn('Zoom constraint failed:', err));
                    }
                  }
                }}
                style={{
                  width: '70px', padding: '10px 0',
                  background: guidedCameraZoomLevel === 0.5 ? '#88b7ff' : 'rgba(255,255,255,0.2)',
                  border: guidedCameraZoomLevel === 0.5 ? 'none' : '1px solid rgba(255,255,255,0.5)',
                  borderRadius: '30px', color: 'white', fontSize: '0.8rem',
                  fontFamily: 'Avenir, sans-serif', cursor: 'pointer',
                  whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'center',
                }}>
                {guidedCameraZoomLevel === 0.5 ? '0.5x' : '1x'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* GUIDED MOTION PREVIEW MODAL - Fullscreen with no black bars */}
{showGuidedMotionPreview && guidedMotionPreviewBlobUrl && (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: '#000', zIndex: 3001, display: 'flex', flexDirection: 'column',
  }}>
    {/* Fullscreen video - using object-fit cover like main camera preview */}
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: '#000', overflow: 'hidden',
    }}>
      <video
        src={guidedMotionPreviewBlobUrl}
        autoPlay
        loop
        muted
        playsInline
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',  // Changed from 'contain' to 'cover' for true fullscreen
          background: '#000'
        }}
      />
      
      {/* Bottom overlay controls - matching glass styling */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(107, 105, 105, 0.15)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '20px 20px calc(55px + env(safe-area-inset-bottom, 0px))',
        zIndex: 10,
      }}>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <div style={{ marginBottom: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.9rem', fontFamily: 'Avenir, sans-serif', color: 'white', marginBottom: '4px', fontWeight: 500 }}>
              Motion Paths Recorded
            </div>
            <div style={{ fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif', color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <span>Duration: {guidedMotionDuration.toFixed(1)}s</span>
              {guidedMotionDuration < 7 && (
                <span style={{ color: '#FFA500', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <IoWarningOutline size={13} />
                  Min 7s required
                </span>
              )}
              {guidedMotionDuration >= 7 && (
                <span style={{ color: '#4CAF50' }}>Ready</span>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleReRecordGuidedMotion} style={{
              flex: 1, padding: '12px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '30px', color: 'white',
              fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem',
              cursor: 'pointer', transition: 'all 0.2s ease',
            }}>
              Re-Record
            </button>
            <button
  onClick={handleConfirmGuidedMotion}
  disabled={guidedMotionDuration < 7 || isUploadingMotion}
  style={{
    flex: 1, padding: '12px',
    background: (guidedMotionDuration < 7 || isUploadingMotion) ? 'rgba(136, 183, 255, 0.4)' : '#88b7ff',
    border: 'none', borderRadius: '30px', color: 'white',
    fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem',
    cursor: (guidedMotionDuration < 7 || isUploadingMotion) ? 'not-allowed' : 'pointer',
    fontWeight: 500, transition: 'all 0.2s ease',
    boxShadow: (guidedMotionDuration < 7 || isUploadingMotion) ? 'none' : '0 4px 15px rgba(136, 183, 255, 0.3)',
    opacity: (guidedMotionDuration < 7 || isUploadingMotion) ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  }}
>
  {isUploadingMotion ? (
    <>
      <div style={{
        width: '16px',
        height: '16px',
        border: '2px solid white',
        borderTop: '2px solid transparent',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      Uploading...
    </>
  ) : (
    guidedMotionDuration < 7 ? `Need ${(7 - guidedMotionDuration).toFixed(1)}s (min 7s required)` : 'Use This Motion'
  )}
</button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideLeft {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-8px); }
        }
        @keyframes slideRight {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(8px); }
        }
        @keyframes slideUp {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes slideDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
        @keyframes blink {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.2; }
        }
        @keyframes marching {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -36; }
        }
        @keyframes pulse-icon {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }

        /* Add these to the existing style tag */
        .guided-camera-video {
        transition: opacity 0.2s ease;
        }

        .guided-camera-preview {
        transition: background-color 0.2s ease;
        }

        /* Prevent white flash during transitions */
        .guided-camera-overlay {
        background-color: #000;
        }
        
        .guided-camera-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #000;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding-top: env(safe-area-inset-top, 0px);
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        .guided-camera-preview {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
        }
        .guided-camera-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .guided-inputs-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          top: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: calc(80px + env(safe-area-inset-top, 0px)) 20px calc(55px + env(safe-area-inset-bottom, 20px)) 20px;
          z-index: 10;
          pointer-events: none;
        }
        .guided-cards-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          pointer-events: none;
          flex: 1;
          margin-top: -10px;
          transform: translateY(0);
          min-height: 0;
          max-height: 100%;
        }
        .guided-input-card {
          background: rgba(107, 105, 105, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.25);
          width: 100%;
          max-width: 320px;
          pointer-events: auto;
          transition: all 0.3s ease;
          transform: scale(1);
          transform-origin: center center;
        }
        .guided-button-container {
          width: 100%;
          max-width: 320px;
          pointer-events: auto;
          flex-shrink: 0;
          display: flex;
          flex-direction: row;
          gap: 12px;
          margin-top: 16px;
        }
        .guided-action-btn {
          flex: 1;
          padding: 14px;
          border-radius: 40px;
          font-family: 'Avenir', sans-serif;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          pointer-events: auto;
          text-align: center;
          white-space: nowrap;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
        }
        .guided-action-btn.continue-btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .guided-input-label {
          font-family: 'Avenir', sans-serif;
          font-size: 0.75rem;
          color: white;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .guided-image-upload-area {
          width: 100%;
          display: flex;
          justify-content: center;
          margin-bottom: 12px;
        }
        .guided-image-box {
          width: 120px;
          height: 213px;
          background: rgba(29, 102, 179, 0.24);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
          position: relative;
          transition: all 0.2s ease;
          border: 2px solid transparent;
        }
        .guided-image-box.has-image {
          border: 2px solid #88b7ff;
          background: rgba(136, 183, 255, 0.2);
        }
        .guided-image-box.marching-ants > svg {
          animation: pulse-icon 1.2s ease-in-out infinite;
        }
        .marching-ants-border {
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          pointer-events: none;
          border-radius: 18px;
          z-index: 2;
          overflow: visible;
        }
        .marching-ants-border svg {
          width: 100%;
          height: 100%;
          display: block;
          overflow: visible;
        }
        .marching-ants-border rect {
          fill: none;
          stroke: #88b7ff;
          stroke-width: 4px;
          stroke-dasharray: 14 6;
          stroke-dashoffset: 0;
          animation: marching 0.5s linear infinite;
        }
        .guided-motion-trigger-box {
          width: 120px;
          height: 213px;
          background: rgba(29, 102, 179, 0.24);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
          position: relative;
          transition: all 0.2s ease;
          margin: 0 auto;
          border: 2px solid transparent;
        }
        .guided-motion-trigger-box.disabled {
          border: 2px dashed rgba(172, 172, 172, 0.7);
          opacity: 0.5;
          cursor: not-allowed;
        }
        .guided-motion-trigger-box.marching-ants {
          border: 2px solid transparent;
          position: relative;
        }
        .guided-motion-trigger-box.marching-ants > svg:first-of-type {
          animation: pulse-icon 1.2s ease-in-out infinite;
        }
        .guided-motion-container {
          position: relative;
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          min-height: 213px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .guided-motion-container.expanded {
          min-height: 280px;
        }
        .guided-input-card.motion-expanded {
          margin-top: -15px;
          transform: scale(0.87);
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        .guided-motion-buttons-wrapper {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          margin-top: 0;
          width: 100%;
        }
        .guided-motion-buttons-wrapper.expanded {
          max-height: 80px;
          opacity: 1;
          margin-top: 16px;
        }
        .guided-motion-area {
          display: flex;
          gap: 12px;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .guided-motion-button {
          flex: 1;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .guided-motion-button span {
          font-size: 0.7rem;
          color: white;
        }
        .guided-back-btn {
          position: absolute;
          top: 20px;
          left: 20px;
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: 30px;
          padding: 10px 16px;
          color: white;
          font-family: 'Avenir', sans-serif;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          z-index: 20;
          backdrop-filter: blur(5px);
        }
        .guided-title {
          position: absolute;
          top: 20px;
          left: 0;
          right: 0;
          text-align: center;
          color: white;
          font-family: 'Avenir', sans-serif;
          font-size: 1rem;
          font-weight: 500;
          z-index: 20;
          pointer-events: none;
        }
      `}</style>
    </>
  );
};

export default GuidedCameraOverlay;